import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { storageAPI } from '../services/api';
import { sanitizeText } from '../lib/htmlSanitizer';
import './Layout.css';
import { prefetchPage } from '../utils/prefetch';
import {
  Home,
  Map,
  MessageSquare,
  BarChart3,
  Users,
  Wrench,
  Moon,
  Sun,
  CircleHelp,
  Headset,
  Mail,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SPRING, SPRING_FAST } from './ui/motion';
import { isSuperAdminClient } from '../lib/superAdmin';
import DriftingMoon from './DriftingMoon';
import { useBackgroundEffects } from '../contexts/BackgroundEffectsContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import BrandLogo from './BrandLogo';
import DemoJourneyToggle from './dev/DemoJourneyToggle';
import PlanIndicator from './billing/PlanIndicator';
import PlanCard from './billing/PlanCard';
import NotificationBell from './notifications/NotificationBell';
import { hasCompletedOnboarding as hasCompletedOnboardingUtil } from '../utils/onboarding';

/* The product tour pulls driver.js and its stylesheet — 21KB of the initial
 * chunk (measured 2026-09-11) for something most page loads never run. Lazy, so
 * it is fetched only when the tour is actually eligible to show. It renders
 * nothing until it starts, so a null fallback is the whole of the loading UI. */
const AppTour = React.lazy(() => import('./tour/AppTour'));

interface LayoutProps {
  children: React.ReactNode;
}

/* Under this width the sidebar is an off-canvas drawer and a top bar carries
   the logo and the menu button. Must match the Layout.css breakpoint. */
const PHONE_MQ = '(max-width: 899px)';

/* Lucide icons at the shell size: 18px, 1.75 stroke, currentColor. */
const ICON = { size: 18, strokeWidth: 1.75, className: 'lnr-icon', 'aria-hidden': true } as const;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  // Persisted like the theme toggle sitting a few pixels away — previously
  // dark mode survived a reload and the collapse state didn't, which read as
  // arbitrary. Same lazy-initialiser + write-on-change shape as
  // DarkModeContext, and tolerant of storage being unavailable.
  const SIDEBAR_COLLAPSED_KEY = 'lanura_sidebar_collapsed';
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      /* non-fatal */
    }
  }, [sidebarCollapsed]);
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  // Phone shell (CC7): under 900px the sidebar becomes a drawer behind a
  // 56px top bar. Read synchronously so the drawer never flashes open on
  // first paint; framer owns the drawer's x from then on.
  const [isPhone, setIsPhone] = useState<boolean>(() => {
    try {
      return (
        typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_MQ).matches
      );
    } catch {
      return false;
    }
  });
  // Scroll affordance for the nav list. Without it the list just guillotines
  // the item at the boundary, which reads as a broken card rather than "there
  // is more below" — the clipped item cannot carry a border of its own,
  // because its bottom edge is outside the scroll container.
  const navRef = useRef<HTMLUListElement>(null);
  const [navMore, setNavMore] = useState({ above: false, below: false });
  const [userToggledSidebar, setUserToggledSidebar] = useState(false);
  // Route content always rendered immediately — no transition gating.
  const showRoute = true;
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const avatarBlobRef = useRef<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const supportRef = useRef<HTMLDivElement>(null);
  const { enabled: backgroundEffectsEnabled } = useBackgroundEffects();
  const { enabled: darkModeEnabled, toggle: toggleDarkMode } = useDarkMode();
  const isPracticeConversation = location.pathname === '/practice-conversation';

  const avatarUrl =
    (user as { avatarUrl?: string })?.avatarUrl || (user as { avatar?: string })?.avatar || '';

  useEffect(() => {
    const isOurEndpoint =
      avatarUrl === '/api/storage/profile/avatar' ||
      (typeof avatarUrl === 'string' && avatarUrl.endsWith('/storage/profile/avatar'));
    if (!avatarUrl || !isOurEndpoint) {
      if (avatarBlobRef.current) {
        URL.revokeObjectURL(avatarBlobRef.current);
        avatarBlobRef.current = null;
      }
      setAvatarBlobUrl(null);
      return;
    }
    let cancelled = false;
    storageAPI.getProfileAvatarBlob().then((blob) => {
      if (cancelled || !blob) return;
      if (avatarBlobRef.current) URL.revokeObjectURL(avatarBlobRef.current);
      const url = URL.createObjectURL(blob);
      avatarBlobRef.current = url;
      setAvatarBlobUrl(url);
    });
    return () => {
      cancelled = true;
      if (avatarBlobRef.current) {
        URL.revokeObjectURL(avatarBlobRef.current);
        avatarBlobRef.current = null;
      }
      setAvatarBlobUrl(null);
    };
  }, [avatarUrl]);

  // Close the support popover on outside-click / Escape. Listeners are
  // only attached while the popover is open (mirrors the select.tsx idiom).
  useEffect(() => {
    if (!supportOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (supportRef.current && target && !supportRef.current.contains(target)) {
        setSupportOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSupportOpen(false);
    };
    document.addEventListener('mousedown', onDocClick, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [supportOpen]);

  // Route transition handled by AnimatePresence key={location.pathname} — no manual show/hide needed.

  useEffect(() => {
    // Auto-collapse the sidebar on smaller screens (but don't fight the user once they've toggled it).
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mqCompact = window.matchMedia('(max-width: 1100px)');
    const mqMobile = window.matchMedia(PHONE_MQ);

    const apply = () => {
      setIsPhone(mqMobile.matches);
      // Mobile: keep sidebar as an off-canvas drawer (not "collapsed")
      if (mqMobile.matches) {
        setSidebarCollapsed(false);
        setSidebarOpenMobile(false);
        return;
      }

      // Tablet-ish: collapse to icon-only by default unless the user explicitly changed it.
      if (!userToggledSidebar) {
        setSidebarCollapsed(mqCompact.matches);
      }
    };

    apply();
    mqCompact.addEventListener?.('change', apply);
    mqMobile.addEventListener?.('change', apply);
    return () => {
      mqCompact.removeEventListener?.('change', apply);
      mqMobile.removeEventListener?.('change', apply);
    };
  }, [userToggledSidebar]);

  // Owners with an expired trial AND no active subscription can only
  // navigate to Dashboard + Subscription from the sidebar. Other items
  // render disabled with a "Subscribe to unlock" tooltip — click on a
  // disabled item navigates to /subscription as a hint. Backend gating
  // (backend/middleware/auth.js) is the real enforcement; this is UX
  // signalling so the disabled items don't silently 402.
  const isOwner = (user as any)?.role === 'owner';
  const trialDaysRemaining =
    typeof (user as any)?.trialDaysRemaining === 'number' ? (user as any).trialDaysRemaining : null;
  const subscriptionActive = !!(user as any)?.subscription?.active;
  const ownerTrialExpiredNoSub =
    isOwner &&
    (user as any)?.accountStatus === 'trial' &&
    trialDaysRemaining !== null &&
    trialDaysRemaining <= 0 &&
    !subscriptionActive;

  // Paths that stay accessible even when the owner's trial has expired
  // without a subscription. Mirrors the backend allowlist (Dashboard +
  // Subscription are the two sidebar-reachable allowed routes; the rest
  // — Settings, Referrals tab, etc. — sit under their own nav surfaces).
  const ALLOWED_WHEN_EXPIRED_OWNER = new Set(['/dashboard', '/subscription']);

  // Pages that are empty/meaningless before the baseline diagnostic — locked
  // (with a 🔒 + tooltip) until onboarding is done, mirroring the route guards
  // in App.tsx (OnboardingRoute → LockedPage). Practice stays open so users
  // can still practise; Dashboard is the landing/redirect target.
  const ONBOARDING_LOCKED_PATHS = new Set(['/pathways', '/progress']);
  const onboardingIncomplete = !hasCompletedOnboardingUtil(user);

  /* N1, Jenny's architecture doc: Home, Career paths, Practice, My progress
     and Team, with Settings in the account box below. `match` is every route
     an entry stands for, so it stays lit across the tabs of its hub
     (components/nav/HubTabs): Practice plans is part of Practice, and People,
     Insights are Team. Overview is the manager's Home (MH1). Subscription and Scoring left the sidebar
     for Settings (N6, MS10): Billing links to the subscription, and Workspace
     to the scoring framework. Both routes are unchanged. */
  const navigationItems: Array<{ path: string; icon: any; label: string; match?: string[] }> = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/pathways', icon: Map, label: 'Career paths' },
    {
      path: '/practice',
      icon: MessageSquare,
      label: 'Practice',
      match: ['/practice', '/team/plans', '/team/assignments'],
    },
    { path: '/progress', icon: BarChart3, label: 'My progress' },
    ...(can('manageTeam')
      ? [
          {
            // Opens on Insights, the first of Team's tabs (TM1).
            path: '/team/analytics',
            icon: Users,
            label: 'Team',
            match: ['/team/analytics', '/team/members'],
          },
        ]
      : []),
    /* Internal Console (N5): Lanura's own tools, lifted out of the client's
       Settings. The pinned superAdmin only, on every host, as the Settings
       group it replaces was. Cosmetic like the link above: the page and its
       endpoints re-check. */
    ...(isSuperAdminClient(user)
      ? [{ path: '/console', icon: Wrench, label: 'Internal console' }]
      : []),
  ];

  // Recomputed on scroll and on any size change (window resize, collapse,
  // a role gaining a nav item). ResizeObserver on the list covers the last
  // two without a second effect.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => {
      const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
      setNavMore({ above: el.scrollTop > 2, below: remaining > 2 });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [navigationItems.length, sidebarCollapsed]);

  const navTourKey = (path: string) => {
    if (path === '/dashboard') return 'nav-dashboard';
    if (path === '/practice') return 'nav-practice';
    if (path === '/progress') return 'nav-progress';
    if (path === '/pathways') return 'nav-pathways';
    return undefined;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setSidebarOpenMobile(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    const item = navigationItems.find((i) => i.path === path);
    return (item?.match || [path]).some((p) => location.pathname.startsWith(p));
  };

  return (
    <div className="app-container">
      <React.Suspense fallback={null}>
        <AppTour user={user} hasCompletedOnboarding={hasCompletedOnboardingUtil(user)} />
      </React.Suspense>
      {/* Sidebar. On phones it is a drawer: framer drives x on SPRING and the
          backdrop fades on SPRING_FAST; on desktop x is pinned at 0 with no
          transition and CSS animates the collapse width. */}
      <AnimatePresence>
        {sidebarOpenMobile && (
          <motion.div
            className="sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={SPRING_FAST}
            onClick={() => setSidebarOpenMobile(false)}
          />
        )}
      </AnimatePresence>
      <motion.nav
        className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarOpenMobile ? 'mobile-open' : ''}`}
        aria-label="Primary navigation"
        aria-hidden={isPhone && !sidebarOpenMobile ? true : undefined}
        inert={isPhone && !sidebarOpenMobile ? true : undefined}
        initial={false}
        animate={isPhone ? { x: sidebarOpenMobile ? 0 : '-100%' } : { x: 0 }}
        transition={isPhone ? SPRING : { duration: 0 }}
      >
        <div className="sidebar-head">
          <div
            className={`sidebar-brand ${sidebarCollapsed ? 'collapsed' : ''}`}
            onClick={() => handleNavigation('/dashboard')}
            role="button"
            tabIndex={0}
          >
            <BrandLogo
              width={sidebarCollapsed ? 44 : 170}
              height={44}
              iconOnly={sidebarCollapsed}
            />
          </div>
          {isPhone && (
            <button
              type="button"
              className="icon-btn sidebar-close"
              aria-label="Close navigation menu"
              onClick={() => setSidebarOpenMobile(false)}
            >
              <X {...ICON} />
            </button>
          )}
        </div>

        <ul
          ref={navRef}
          className={`nav-menu${navMore.above ? ' nav-menu--more-above' : ''}${navMore.below ? ' nav-menu--more-below' : ''}`}
          style={{ marginTop: 16 }}
        >
          {navigationItems.map((item) => {
            // Two independent lock reasons. Billing takes precedence over the
            // onboarding lock when both apply (an expired-trial owner is sent
            // to Subscription, not the diagnostic).
            const lockedForBilling =
              ownerTrialExpiredNoSub && !ALLOWED_WHEN_EXPIRED_OWNER.has(item.path);
            const lockedForOnboarding =
              onboardingIncomplete && ONBOARDING_LOCKED_PATHS.has(item.path);
            const isDisabled = lockedForBilling || lockedForOnboarding;
            const lockRedirect = lockedForBilling ? '/subscription' : '/baseline-quiz';
            const tooltip = isDisabled
              ? lockedForBilling
                ? 'Subscribe to unlock this'
                : 'Complete your baseline diagnostic first to unlock'
              : sidebarCollapsed
                ? item.label
                : '';
            return (
              <li
                key={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}${isDisabled ? ' nav-item--disabled' : ''}`}
                onClick={() =>
                  isDisabled ? handleNavigation(lockRedirect) : handleNavigation(item.path)
                }
                onMouseEnter={() => !isDisabled && prefetchPage(item.path)}
                title={tooltip}
                data-nav-tooltip={isDisabled ? tooltip : undefined}
                data-tour={navTourKey(item.path)}
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-disabled={isDisabled || undefined}
              >
                {isActive(item.path) && (
                  <motion.span
                    className="nav-item__indicator"
                    layoutId="sidebar-active-row"
                    transition={SPRING}
                    aria-hidden="true"
                  />
                )}
                <span className="nav-icon">
                  <item.icon {...ICON} />
                </span>
                {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                {isDisabled && !sidebarCollapsed && (
                  <span className="nav-lock" aria-hidden="true">
                    <Lock size={12} strokeWidth={2} className="lnr-icon" />
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* Utility icons: notifications + dark mode + tour — sit above the divider (above user info) */}
        <div className="sidebar-utilRow">
          <NotificationBell enabled={!!user} />
          <button
            type="button"
            className="icon-btn sidebar-utilBtn"
            data-tooltip={darkModeEnabled ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={darkModeEnabled ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleDarkMode}
          >
            {darkModeEnabled ? <Sun {...ICON} /> : <Moon {...ICON} />}
          </button>
          <button
            type="button"
            className="icon-btn sidebar-utilBtn"
            data-tooltip="Tour mode - guided walkthrough of Lanura"
            aria-label="Tour mode - guided walkthrough of Lanura"
            data-tour="tour-icon"
            onClick={() => {
              try {
                window.dispatchEvent(new CustomEvent('lanura:tour:start'));
              } catch {}
              setSidebarOpenMobile(false);
            }}
          >
            <CircleHelp {...ICON} />
          </button>
          <div className="support-anchor" ref={supportRef}>
            <button
              type="button"
              className="icon-btn sidebar-utilBtn"
              data-tooltip="Support - get help"
              aria-label="Support - get help"
              aria-haspopup="menu"
              aria-expanded={supportOpen}
              onClick={() => setSupportOpen((v) => !v)}
            >
              <Headset {...ICON} />
            </button>
            <AnimatePresence>
              {supportOpen && (
                <motion.div
                  className="support-menu"
                  role="menu"
                  aria-label="Support"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0.12 } }}
                  transition={SPRING_FAST}
                >
                  <a
                    className="support-menu-item"
                    role="menuitem"
                    href="mailto:team@lanura.ai"
                    onClick={() => {
                      setSupportOpen(false);
                      setSidebarOpenMobile(false);
                    }}
                  >
                    <span className="support-menu-item-icon" aria-hidden="true">
                      <Mail size={16} strokeWidth={1.75} className="lnr-icon" aria-hidden="true" />
                    </span>
                    <span className="support-menu-item-text">
                      <span className="support-menu-item-title">Email us</span>
                      <span className="support-menu-item-sub">team@lanura.ai</span>
                    </span>
                  </a>
                  <a
                    className="support-menu-item"
                    role="menuitem"
                    href="https://discord.gg/ds655NaP"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      setSupportOpen(false);
                      setSidebarOpenMobile(false);
                    }}
                  >
                    <span className="support-menu-item-icon" aria-hidden="true">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M20.317 4.369A19.79 19.79 0 0 0 15.885 3a13.86 13.86 0 0 0-.617 1.27 18.27 18.27 0 0 0-5.535 0A13.84 13.84 0 0 0 9.11 3a19.74 19.74 0 0 0-4.435 1.37C1.87 8.57 1.11 12.66 1.49 16.69a19.93 19.93 0 0 0 6.073 3.08c.49-.67.927-1.38 1.302-2.13-.713-.27-1.396-.6-2.043-.99.171-.125.339-.255.5-.39a14.23 14.23 0 0 0 12.36 0c.163.14.331.27.5.39-.648.39-1.332.72-2.045.99.375.75.812 1.46 1.301 2.13a19.9 19.9 0 0 0 6.075-3.08c.448-4.67-.766-8.72-3.196-12.32ZM8.02 14.21c-1.182 0-2.157-1.085-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.955 2.42-2.157 2.42Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.335-.946 2.42-2.157 2.42Z" />
                      </svg>
                    </span>
                    <span className="support-menu-item-text">
                      <span className="support-menu-item-title">Join our Discord</span>
                      <span className="support-menu-item-sub">Chat with the community</span>
                    </span>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="sidebar-footer">
          {/* Plan card — standalone for owners, sits above the account
              card so billing state and account identity don't share a
              single bordered container. Hidden when collapsed (no room
              for label) and when no active sub (PlanCard returns null). */}
          <div className="account-card">
            {/* One flat card (CC7): the plan row sits inside the account
                card above a hairline, instead of a second bordered box. */}
            {!sidebarCollapsed && <PlanCard user={user} />}
            <div className="account-top mt-1">
              <div className="account-avatar" aria-hidden="true">
                {avatarBlobUrl || (avatarUrl && avatarUrl.startsWith('http')) ? (
                  <img src={avatarBlobUrl || avatarUrl} alt="" className="account-avatar-img" />
                ) : (
                  (user?.name || user?.email || 'U').charAt(0).toUpperCase()
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="account-meta">
                  <div className="account-name">{sanitizeText(user?.name || 'Guest')}</div>
                  <div className="account-email">{sanitizeText(user?.email || '')}</div>
                  {/* Teammate-only signal — tier-colored dot, no plan
                      name (decision #8). Owners get the standalone
                      PlanCard above instead. */}
                  <PlanIndicator user={user} />
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="account-actions">
                <button
                  className="account-btn"
                  onClick={() => handleNavigation('/settings')}
                  data-tour="settings-btn"
                >
                  Settings
                </button>
                <button className="account-logout" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
            {/* Collapsed sidebar previously hid .account-actions outright, which
                left no way to reach Settings or sign out at all — and the
                sidebar auto-collapses at ≤1100px, so that was the default state
                on small laptops and tablets. Icon-only equivalents, tooltipped
                the same way as the utility row above. */}
            {sidebarCollapsed && (
              <div className="account-actions-collapsed">
                <button
                  type="button"
                  className="icon-btn sidebar-utilBtn"
                  data-tooltip="Settings"
                  aria-label="Settings"
                  data-tour="settings-btn"
                  onClick={() => handleNavigation('/settings')}
                >
                  <Settings {...ICON} />
                </button>
                <button
                  type="button"
                  className="icon-btn sidebar-utilBtn account-logout-icon"
                  data-tooltip="Log out"
                  aria-label="Log out"
                  onClick={handleLogout}
                >
                  <LogOut {...ICON} />
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          className="sidebar-toggle"
          onClick={() => {
            setUserToggledSidebar(true);
            setSidebarCollapsed(!sidebarCollapsed);
          }}
          aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={sidebarCollapsed ? 'Expand' : 'Collapse'}
        >
          <span className="sidebar-toggle-icon" aria-hidden="true">
            {sidebarCollapsed ? (
              <ChevronRight size={14} strokeWidth={2} className="lnr-icon" />
            ) : (
              <ChevronLeft size={14} strokeWidth={2} className="lnr-icon" />
            )}
          </span>
        </button>
      </motion.nav>

      {/* Main Content */}
      <main className="main-content">
        {/* Phone top bar (CC7): 56px of the landing header material, logo
            left and the menu button right, so nothing in the page can sit
            under the hamburger. Hidden by CSS from 900px up. */}
        <header className="app-topbar">
          <BrandLogo width={118} height={32} className="app-topbar__logo" />
          <button
            className="mobile-sidebar-toggle icon-btn"
            onClick={() => setSidebarOpenMobile((v) => !v)}
            aria-label={sidebarOpenMobile ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={sidebarOpenMobile}
          >
            {sidebarOpenMobile ? (
              <X size={20} strokeWidth={1.75} className="lnr-icon" aria-hidden="true" />
            ) : (
              <Menu size={20} strokeWidth={1.75} className="lnr-icon" aria-hidden="true" />
            )}
          </button>
        </header>
        {backgroundEffectsEnabled && <DriftingMoon />}
        {/* Route content is not wrapped in motion here: the Suspense
            boundary (App.tsx) must show PageSkeleton inside a visible parent.
            The per-route entrance lives in PageTransition, which mounts with
            the resolved page. */}
        {showRoute && (
          <div className={`page-content ${isPracticeConversation ? 'page-content--practice' : ''}`}>
            {children}
          </div>
        )}
        {/* Dev-only journey switcher — self-gates to test@gmail.com, renders
            null for everyone else. Lets the demo account flip the dashboard
            between new-user and in-journey views from any page. */}
        <DemoJourneyToggle />
      </main>
    </div>
  );
};

export default Layout;
